
import React, { useState } from "react";
import { Hidden, IconButton, alpha } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { ReactComponent as Calender } from "../../../assets/images/calendar.svg";
import { ReactComponent as Clock } from "../../../assets/images/clock.svg";
import dayjs from "dayjs"; // Import dayjs
import theme, { mobileTypography, typography } from "src/config/theme";
import { ReactComponent as ArrowDown } from '../../../assets/images/arrow-down.svg';
import { ReactComponent as ArrowLeft } from '../../../assets/images/arrow-left.svg';
import { ReactComponent as ArrowRight } from '../../../assets/images/arrow-right.svg';
import useResponsive from "src/components/hooks/useResponsive";

// Stable arrow icon components — created once, not per-render
const CustomArrowDownSmall = () => <ArrowDown width={18} height={18} />;
const CustomArrowDownLarge = () => <ArrowDown width={24} height={24} />;

const parseTimeValue = (val) => {
  if (!val) return null;
  if (dayjs.isDayjs(val)) return val.isValid() ? val : null;
  if (typeof val === 'string') {
    const formats = ['hh:mm A', 'h:mm A', 'HH:mm', 'h:mma', 'HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DDTHH:mm:ss'];
    for (const fmt of formats) {
      const parsed = dayjs(val, fmt);
      if (parsed.isValid()) return parsed;
    }
  }
  const fallback = dayjs(val);
  return fallback.isValid() ? fallback : null;
};

const DateTimePicker = ({
  label,
  value,
  onChange,
  type = "date",
  sx = {},
  minDate = null,
  maxDate = null,
  disabled = false,
  error = false,
  helperText = "",
  name,
  onBlur,
  onAccept,
  format,
  ampm = true,
  minutesStep = 15,
}) => {
  const [open, setOpen] = useState(false);
  const { isMobile, isTablet } = useResponsive();
  const CustomArrowDownIcon = isMobile || isTablet ? CustomArrowDownSmall : CustomArrowDownLarge;

  const effectiveTimeFormat = format || (ampm ? "hh:mm A" : "HH:mm");

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {type === "date" ? (
        <DatePicker
          disabled={disabled}
          onAccept={onAccept}
          value={value ? (dayjs.isDayjs(value) ? value : (dayjs(value).isValid() ? dayjs(value) : null)) : null}
          onChange={(newValue) => {
            if (newValue?.isValid()) {
              onChange(newValue?.format('YYYY-MM-DD'));
            } else if (newValue === null) {
              onChange(null);
            }
          }}
          views={['year', 'month', 'day']}
          format={format || "DD/MM/YYYY"}
          minDate={minDate ? dayjs(minDate) : null}
          maxDate={maxDate ? dayjs(maxDate) : null}
          open={open}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          slots={{
            switchViewIcon: CustomArrowDownIcon,
            leftArrowIcon: ArrowLeft,
            rightArrowIcon: ArrowRight
          }}
          slotProps={{
            textField: {
              name: name,
              fullWidth: true,
              label: label,
              error: error,
              helperText: helperText,
              onBlur: onBlur,
              variant: 'outlined',
              InputLabelProps: { shrink: true },
              sx: {
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  height: "52px",
                  display: "flex",
                  alignItems: "center",
                  ...sx,
                },
                "& .MuiInputBase-input.MuiOutlinedInput-input": {
                  ...(isMobile || isTablet ? (theme?.mobileTypography?.body2 || mobileTypography.body2) : (theme?.typography?.h5 || typography.h5)),
                },
              },
              InputProps: {
                endAdornment: (
                  <IconButton
                    width={isMobile || isTablet ? 18 : 24}
                    height={isMobile || isTablet ? 18 : 24}
                    onClick={() => { if (!open) setOpen(true) }}
                    sx={{
                      padding: 0,
                      minWidth: "auto",
                    }}
                  >
                    <Calender
                      width={isMobile || isTablet ? 18 : 24}
                      height={isMobile || isTablet ? 18 : 24}
                    />
                  </IconButton>
                ),
              },
            }
          }}
        />
      ) : (
        <TimePicker
          disabled={disabled}
          value={parseTimeValue(value)}
          onChange={(newValue) => {
            if (newValue?.isValid()) {
              onChange(newValue);
            } else if (newValue === null) {
              onChange(null);
            }
          }}
          onAccept={(newValue) => {
            setOpen(false);
            if (onAccept && newValue) onAccept(newValue);
          }}
          format={effectiveTimeFormat}
          ampm={ampm}
          minutesStep={minutesStep}
          open={open}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          slotProps={{
            textField: {
              name: name,
              fullWidth: true,
              label: label,
              error: error,
              helperText: helperText,
              onBlur: onBlur,
              variant: 'outlined',
              InputLabelProps: { shrink: true },
              sx: {
                height: "52px",
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  height: "52px",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "12px",
                  ...sx,
                },
                "& .MuiInputBase-input.MuiOutlinedInput-input": {
                  ...(isMobile || isTablet ? (theme?.mobileTypography?.body2 || mobileTypography.body2) : (theme?.typography?.h5 || typography.h5)),
                },
              },
              InputProps: {
                endAdornment: (
                  <IconButton
                    width={isMobile || isTablet ? 18 : 24}
                    height={isMobile || isTablet ? 18 : 24}
                    onClick={() => { if (!open) setOpen(true) }}
                    sx={{
                      padding: 0,
                      minWidth: "auto",
                      "& .MuiSvgIcon-root": {
                        fill: "red",
                      },
                    }}
                  >
                    <Clock
                      width={isMobile || isTablet ? 18 : 24}
                      height={isMobile || isTablet ? 18 : 24}
                    />
                  </IconButton>
                ),
              },
            },
          }}
        />
      )}
    </LocalizationProvider>
  );
};
export default DateTimePicker;








